import { useSearchParams } from '@remix-run/react';
import { useRef, useState } from 'react';
import { useSpinDelay } from 'spin-delay';
import { useIntersectionObserver } from '~/hooks/useIntersectionObserver';
import { BlinkFrame } from './BlinkFrame';
import { ErrorMessage } from './error';
import { Loading } from './Loading';
import { StickyTopbar, VgApp } from './preview/vg';
import { Search } from './Search';

type Props = {
    initialAd?: string;
};

export function Preview({ initialAd: fallbackAdId }: Props) {
    const [loading, setLoading] = useState(true);
    const [contentReady, setContentReady] = useState(false);
    const [overrideMode, setOverrideMode] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const [searchParams, setSearchParams] = useSearchParams();
    const urlAdId = searchParams.get('finnkode') || '';
    const [overrideAdId, setOverrideAdId] = useState<string>(urlAdId);

    const showLoader = useSpinDelay(loading && !errorMessage, {
        delay: 0,
        minDuration: 2000,
    });

    const [showRecommendation, setShowRecommendation] = useState(true);

    const ref = useRef<HTMLDivElement>(null);
    const entry = useIntersectionObserver(ref, {
        freezeOnceVisible: true,
        rootMargin: '200px',
        threshold: 0,
    });
    const isVisible = !!entry?.isIntersecting;

    let preview: JSX.Element | null = null;

    if (isVisible) {
        if (overrideAdId) {
            preview = (
                <BlinkFrame
                    adId={overrideAdId}
                    channel="finn-blink-landingpage"
                    position="preview"
                    onLoad={() => {
                        setLoading(false);
                        setContentReady(true);
                    }}
                    onError={() => {
                        setLoading(false);
                        setErrorMessage(
                            'Vi klarer ikke å vise deg denne forhåndsvisningen akkurat nå. Prøv en annen FINN-kode eller prøv igjen senere.',
                        );
                    }}
                />
            );
        } else if (overrideMode === false) {
            if (showRecommendation) {
                preview = (
                    <BlinkFrame
                        channel="finn-blink-landingpage"
                        position="preview"
                        onLoad={() => {
                            setLoading(false);
                            setContentReady(true);
                        }}
                        onError={() => {
                            setLoading(false);
                            setShowRecommendation(false);
                        }}
                    />
                );
            } else {
                preview = (
                    <BlinkFrame
                        adId={fallbackAdId}
                        channel="finn-blink-landingpage"
                        position="preview"
                        onLoad={() => {
                            setLoading(false);
                            setContentReady(true);
                        }}
                        onError={() => {
                            setLoading(false);
                            setErrorMessage('Vi klarer ikke å vise deg en forhåndsvisningen akkurat nå. Prøv igjen senere.');
                        }}
                    />
                );
            }
        }
    }

    return (
        <>
            <div
                ref={ref}
                className={`preview flex w-full flex-col overflow-hidden py-32 md:py-64 ${
                    contentReady || !!overrideAdId ? 'active' : 'inactive'
                }`}>
                <div className="relative h-full">
                    <div className="relative">
                        <Search
                            value={overrideAdId}
                            loading={showLoader}
                            onSubmit={(ad) => {
                                setLoading(true);
                                if (parseInt(ad)) {
                                    setErrorMessage(undefined);
                                    setOverrideAdId(ad);
                                    setSearchParams({ finnkode: ad }, { preventScrollReset: true });
                                } else {
                                    console.log('Not an ad', ad);
                                }
                            }}
                        />

                        {showLoader && <Loading height={48} className="absolute bottom-0" />}
                        {errorMessage && (
                            <div className="card-content mx-auto flex w-full gap-8 px-16 md:gap-16">
                                <ErrorMessage message={errorMessage} />
                            </div>
                        )}
                    </div>
                    {!errorMessage && (
                        <div
                            style={{
                                zIndex: showLoader || !contentReady ? -1 : 1,
                            }}
                            className={`absolute-center start-hidden h-full w-full overflow-hidden transition-opacity ${
                                !showLoader && contentReady ? 'show' : ''
                            }`}>
                            <div className="experience blink relative">
                                <div className="vg-wrapper-grid fade blur-sm">
                                    <div className="item "></div>
                                    <div className="item"></div>
                                    <div className="item "></div>

                                    <div className="item "></div>
                                    <div className="item query centerpiece">
                                        <VgApp className="background" />
                                    </div>
                                    <div className="item "></div>

                                    <div className="item "></div>
                                    <div className="item "></div>
                                    <div className="item "></div>
                                </div>

                                <div className="clip-grid">
                                    <div className="item"></div>
                                    <div className="item"></div>
                                    <div className="item"></div>

                                    <div className="item"></div>
                                    <div className="item clip centerpiece">
                                        <StickyTopbar />
                                        <div className="vg-wrapper-grid">
                                            <div className="item "></div>
                                            <div className="item"></div>
                                            <div className="item "></div>

                                            <div className="item "></div>
                                            <div className="item query centerpiece">
                                                <VgApp>{preview}</VgApp>
                                            </div>
                                            <div className="item "></div>

                                            <div className="item "></div>
                                            <div className="item "></div>
                                            <div className="item "></div>
                                        </div>
                                    </div>
                                    <div className="item"></div>

                                    <div className="item"></div>
                                    <div className="item"></div>
                                    <div className="item"></div>
                                </div>

                                <div className="clip-grid">
                                    <div className="item"></div>
                                    <div className="item"></div>
                                    <div className="item"></div>

                                    <div className="item"></div>
                                    <div className="item framer centerpiece"></div>
                                    <div className="item"></div>

                                    <div className="item"></div>
                                    <div className="item"></div>
                                    <div className="item"></div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {!showLoader && contentReady && (
                <div className="absolute top-0 mx-auto flex w-full pt-128">
                    <button
                        type="button"
                        aria-label="Lukk"
                        className="button text button--pill text-blue-alt-900 outline-none hover:border-blue-alt-900 focus:border-blue-alt-900 active:border-blue-alt-900 ml-auto mr-40 border-transparent bg-green-300 transition-all duration-300 hover:bg-green-300"
                        onClick={() => {
                            setContentReady(false);
                            setOverrideMode(true);
                            setOverrideAdId('');
                            setSearchParams({}, { preventScrollReset: true });
                        }}>
                        <svg className="h-16 w-16 sm:h-24 sm:w-24" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path
                                fill="none"
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2.5"
                                d="M12 12l6 6-6-6-6 6 6-6zm0 0L6 6l6 6 6-6-6 6z"></path>
                        </svg>
                    </button>
                </div>
            )}
        </>
    );
}
